import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../axios";
import { getCountOfInactive, getCountOfNewPatient, notify } from "../../../util/helpers";

const initialState = {
  loading: true, //! This is only for when no other API calls should be made while the state is changing. GLOBAL LOADING STATE.
  flagLoading: false,
  patientData: [],
  patientCount: 0,
  patientNext: null,
  patientPrevious: null,
  redPatient: [],
  yellowPatient: [],
  newPatient: [],
  bhiPatient: [],
  billableBHIPatients: [],
  bhiEligiblePatient: [],
  bhiPatientCount: 0,
  inActivePatient: [],
  invitedPatient: [],
  singlePatientData: {},
  patientNotes: [],
  patientNotesLoading: false,
  caregiverLoading: false,
  caregiverError: false,
  caregiverData: {},
  patientPhysicians: [],
  mrnNumberLoading: false,
  deleteDiagnosisPatientLoading: false,
  deleteDiagnosisPatientError: null,
  updateDiagnosisPatientLoading: false,
  updateDiagnosisPatientError: null,
  updatePatientInfoLoading: false
};

const updatePatientInLists = (state, updatedPatient) => {
  if (!updatedPatient?.id) return;
  const updateList = (list) =>
    (list || []).map((patient) => (patient.id === updatedPatient.id ? { ...patient, ...updatedPatient } : patient));

  state.patientData = updateList(state.patientData);
  state.redPatient = updateList(state.redPatient);
  state.yellowPatient = updateList(state.yellowPatient);
  state.newPatient = updateList(state.newPatient);
  state.bhiPatient = updateList(state.bhiPatient);
  state.inActivePatient = updateList(state.inActivePatient);
  state.bhiEligiblePatient = updateList(state.bhiEligiblePatient);
};

const alterSinglePatientData = (state, { payload }) => {
  state.singlePatientData = payload;
  updatePatientInLists(state, payload);
};

const alterChatStatusLogic = (state, { payload }) => {
  state.singlePatientData.rose_chat = payload;
};

const alterSSRIStatusLogic = (state, { payload }) => {
  state.singlePatientData.ssri_status = payload;
};

export const toggleSSRIStatusLogic = (payload) => {
  return axiosInstance
    .patch(
      `/api/toggle-ssri-status/`,
      JSON.stringify({
        patient_id: payload?.patientID,
        ssri_status: payload?.ssriStatus,
      }),
      {
        headers: {
          Authorization: `JWT ${localStorage.access}`,
          "Content-Type": "application/json",
        },
      }
    )
    .then()
    .catch();
};

export const alterBHIManagers = (patientID, payload) => {
  return axiosInstance
    .patch(`/api/patient/${patientID}/`, JSON.stringify(payload), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
    })
    .then()
    .catch();
};

export const fetchCaregiverDataLogic = (patientID) => {
  return axiosInstance
    .get(`/api/caregiver/${patientID}`, {
      headers: {
        Authorization: `JWT ${localStorage.access}`,
        "Content-Type": "application/json",
      },
    })
    .then()
    .catch();
};

export const fetchClinicAdminDataLogic = (clinicianID) => {
  return axiosInstance
    .get(`/api/clinic-admin/${clinicianID}`, {
      headers: {
        Authorization: `JWT ${localStorage.access}`,
        "Content-Type": "application/json",
      },
    })
    .then()
    .catch();
};

export const changePatientFlagLogic = createAsyncThunk(
  "patient/flag/change",
  (payload) => {
    const patientID = payload?.id;
    delete payload["id"];
    return axiosInstance.request({
      url: `/api/patient/${patientID}/`,
      maxBodyLength: Infinity,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
      data: JSON.stringify(payload),
    });
  }
);

export const patientLogic = createAsyncThunk("patient/fetch", (params) => {
  return axiosInstance.get("/api/patient/", {
    headers: {
      "Content-Type": "application/json",
      Authorization: `JWT ${localStorage.access}`,
    },
    params: params,
  });
});

export const patientLogicSolo = (params) => {
  return axiosInstance.get("/api/patient/", {
    headers: {
      "Content-Type": "application/json",
      Authorization: `JWT ${localStorage.access}`,
    },
    params: params,
  });
};

export const patientNoStateLogic = (params) => {
  return axiosInstance
    .get("/api/patient/", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
      params: params,
    })
    .then()
    .catch();
};

export const bhiPatientLogic = createAsyncThunk(
  "patient/bhi/fetch",
  (params) => {
    return axiosInstance.get("/api/patient/", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
      params: params,
    });
  }
);

export const invitedPatientLogic = createAsyncThunk(
  "patient/fetch/invited",
  (params) => {
    return axiosInstance.get("/api/patient/invite/", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
      params: params,
    });
  }
);

export const invitePatientLogic = async (payload) => {
  return axiosInstance
    .post("/api/patient/create-invite/", JSON.stringify(payload), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
    })
    .then()
    .catch();
};
export const addPatientLogic = async (payload) => {
  return axiosInstance
    .post("/api/patient/register-patient-with-pin/", JSON.stringify(payload), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
    })
    .then()
    .catch();
};
export const addRegistryPatientLogic = async (payload) => {
  return axiosInstance
    .post("/api/patient/v3/register-patient-with-pin/", JSON.stringify(payload), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
    })
    .then()
    .catch();
};

export const inviteBHIPatientLogic = (payload, path, method = "post") => {
  let url = "/api/bhi-patient/";
  if (method === "patch") {
    url += `${path}/`;
  }
  return axiosInstance
    .request({
      url: url,
      maxBodyLength: Infinity,
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
      data: JSON.stringify(payload),
    })
    .then()
    .catch();
};

export const inviteWalkInPatientLogic = async (payload) => {
  //TODO: Implement this API!
  return axiosInstance
    .post("", JSON.stringify(payload), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
    })
    .then()
    .catch();
};

export const multiInvitePatientLogic = async (payload) => {
  //* No stringifying because the payload is of formdata type
  return axiosInstance
    .post("/api/clinic/patient/multi-invite/", payload, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `JWT ${localStorage.access}`,
      },
    })
    .then()
    .catch();
};

export const singlePatientLogic = createAsyncThunk(
  "patient/single",
  ({ id, params = null }) => {
    return axiosInstance.get(`/api/patient/${id}/`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
      params: params,
    });
  }
);

export const fetchCaregiverLogic = createAsyncThunk(
  "patient/caregiver",
  (id) => {
    return axiosInstance.get(`/api/caregiver/${id}/`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
    });
  }
);

export const checkPatientExists = (id) => {
  if (Number.isNaN(id)) {
    return;
  }
  return axiosInstance
    .get(`/api/patient/${id}/`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
    })
    .then()
    .catch();
};

export const fetchPatientNotesLogic = createAsyncThunk(
  "patient/notes/fetch",
  (params) => {
    return axiosInstance.get("/api/diagnosis-data-patient/", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
      params: params,
    });
  }
);

export const fetchDiagnosisDataLogic = createAsyncThunk(
  "patient/diagnosis/fetch",
  (params) => {
    //params = patient=1813&ordering=-created
    return axiosInstance.get("/api/diagnosis-data-patient/", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
      params: params,
    });
  }
);

export const setDiagnosisDataLogic = createAsyncThunk(
  "patient/diagnosis/set",
  (payload) => {
    return axiosInstance.request({
      method: "post",
      url: "/api/diagnosis-data-patient/",
      headers: {
        Authorization: `JWT ${localStorage.access}`,
      },
      data: JSON.stringify(payload),
    });
  }
);

// DELETE: Patient Diagnosis Data
export const deleteDiagnosisDataLogic = createAsyncThunk(
  "patient/diagnosis/delete",
  async (payload) => {
    console.log(payload, 'payload at the time of diagnosis data logic')
    const response = await axiosInstance.request({
      method: "delete",
      url: `/api/diagnosis-data-patient/${payload?.id}/`,
      headers: {
        Authorization: `JWT ${localStorage.access}`,
      },
    });
    return response.data;
  }
);

// UPDATE: Patient Diagnosis Data
export const updateDiagnosisDataLogic = createAsyncThunk(
  "patient/diagnosis/update",
  async (payload) => {
    const response = await axiosInstance.request({
      method: "put",
      url: `/api/diagnosis-data-patient/${payload.id}/`,
      headers: {
        Authorization: `JWT ${localStorage.access}`,
        "Content-Type": "application/json",
      },
      data: JSON.stringify(payload.data),
    });
    return response.data;
  }
);

export const fetchPatientMessageCountLogic = (payload) => {
  return axiosInstance
    .post(
      `api/aco-executive-stats/get-messages-count-per-day/`,
      JSON.stringify(payload),
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${localStorage.access}`,
        },
      }
    )
    .then()
    .catch();
};
export const fetchActivePatientInDateRangeLogic = (payload) => {
  return axiosInstance
    .post(
      `api/aco-executive-stats/get-last-active-patients-count-per-day/`,
      JSON.stringify(payload),
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${localStorage.access}`,
        },
      }
    )
    .then()
    .catch();
};

export const diagnosisDataPatient = (payload) => {
  return axiosInstance
    .post("/api/diagnosis-data-patient/", JSON.stringify(payload), {
      headers: {
        Authorization: `JWT ${localStorage.access}`,
        "Content-Type": "application/json",
      },
    })
    .then()
    .catch();
};

export const updateSelfHarmFlag = (payload) => {
  return axiosInstance
    .post(
      "/api/patient-journal/update-self-harm-journal-flag/",
      JSON.stringify(payload),
      {
        headers: {
          Authorization: `JWT ${localStorage.access}`,
          "Content-Type": "application/json",
        },
      }
    )
    .then()
    .catch();
};

export const setPatientPhysician = createAsyncThunk(
  "patient/physicians/set",
  (payload) => {
    return axiosInstance.post(
      "/api/patient-custom/set-patient-physician/",
      JSON.stringify(payload),
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${localStorage.access}`,
        },
      }
    );
  }
);

export const getPatientPhysicians = createAsyncThunk(
  "patient/physicians/fetch",
  (params) => {
    return axiosInstance.get("/api/patient-custom/get-patient-physician/", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.access}`,
      },
      params: params,
    });
  }
);

export const handleUpdatePatientInfo = createAsyncThunk(
  "patient/info/update",
  async (
    payload,
    thunkAPI
  ) => {
    try {
      console.log(payload, 'payload')
      const response = await axiosInstance.patch(
        `/api/patient/${payload.patientID}/`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${localStorage.access}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || "Failed to update patient info"
      );
    }
  }
);

export const handleMrnNumber = createAsyncThunk(
  "patient/mrn/update",
  async (
    payload,
    thunkAPI
  ) => {
    try {
      const response = await axiosInstance.patch(
        `/api/patient/${payload.patientID}/`,
        { mrn_number: payload.mrnNumber },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${localStorage.access}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || "Failed to update MRN"
      );
    }
  }
);
export const markPatientReviewed = createAsyncThunk(
  "patient/markReviewed",
  async (patientId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/patient-custom/mark-reviewed/`, {
        patient_id: patientId
      }, {
        headers: {
          Authorization: `JWT ${localStorage.access}`,
        },
      });
      return { patientId, ...response.data };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong!");
    }
  }
);

const alertFireFlagLogic = (state, { payload }) => {
  let temp = state.singlePatientData.fire_flag;
  temp = temp.filter((item) => item?.id !== payload);
  state.singlePatientData.fire_flag = temp;
  // update it to all patient list
  const updatedPatientData = state.patientData.map((patient) => {
    if (patient.id === state.singlePatientData?.id) {
      return {
        ...state.singlePatientData,
      };
    }
    return patient;
  });
  state.patientData = updatedPatientData;
};

const flagLookup = {
  "flag change to red": "urgent",
  "flag change to yellow": "medium",
  "flag change to null": "green",
};

const patientAPICalls = {
  [patientLogic.pending]: (state) => {
    state.loading = true;
  },
  [patientLogic.fulfilled]: (state, { payload }) => {
    payload = payload.data;
    const redPatient = [];
    const bhiPatient = [];
    const yellowPatient = [];
    const newPatient = [];
    const inActivePatient = [];
    const bhiEligiblePatient = [];
    payload.results.forEach((item) => {
      if (getCountOfNewPatient(item)) {
        newPatient.push(item);
      }
      if (item["bhi_enabled"]) {
        bhiPatient.push(item);
      }
      if (item["bhi_eligible"] && !item["bhi_enabled"]) {
        bhiEligiblePatient.push(item);
      }
      if (item["flag"] === "YELLOW") {
        yellowPatient.push(item);
      }
      if (item["flag"] === "RED") {
        redPatient.push(item);
      }
      if (getCountOfInactive(item.last_active)) {
        inActivePatient.push(item);
      }
    });
    state.patientData = payload.results;
    state.patientCount = payload.count;
    state.patientNext = payload.next;
    state.patientPrevious = payload.previous;
    state.redPatient = redPatient;
    state.yellowPatient = yellowPatient;
    state.newPatient = newPatient;
    state.bhiPatient = bhiPatient;
    state.inActivePatient = inActivePatient;
    state.bhiEligiblePatient = bhiEligiblePatient;
    state.loading = false;
  },
  [patientLogic.rejected]: (state, action) => {
    state = initialState;
    state.loading = false;
  },

  //* Getting only BHI Patients Promises
  [bhiPatientLogic.pending]: (state) => {
    state.loading = true;
    state.bhiPatient = [];
  },
  [bhiPatientLogic.fulfilled]: (state, { payload }) => {
    state.billableBHIPatients = payload?.data?.results;
    state.bhiPatientCount = payload?.data?.count || 0;
    state.loading = false;
  },
  [bhiPatientLogic.rejected]: (state, action) => {
    state = initialState;
    state.loading = false;
  },

  //* Getting only Invited Patients Promises

  [invitedPatientLogic.pending]: (state) => {
    state.loading = true;
  },
  [invitedPatientLogic.fulfilled]: (state, { payload }) => {
    state.invitedPatient = payload?.data?.results || [];
    state.loading = false;
  },
  [invitedPatientLogic.rejected]: (state, action) => {
    state = initialState;
    state.loading = false;
  },

  //* Getting Patient Physician options based on Clinic.

  [getPatientPhysicians.pending]: (state) => {
    state.patientPhysicians = [];
  },
  [getPatientPhysicians.fulfilled]: (state, { payload }) => {
    state.patientPhysicians = payload?.data || [];
  },
  [getPatientPhysicians.rejected]: (state) => {
    state.patientPhysicians = [];
  },

  //* Setting chosen Patient Physician.

  [setPatientPhysician.pending]: (state) => { },

  [setPatientPhysician.fulfilled]: (state, { payload }) => {
    const selectedPatient = JSON.parse(payload.config.data);
    state.singlePatientData.physician = selectedPatient.name;
  },
  [setPatientPhysician.rejected]: (state) => { },

  //*Caregiver Promises
  [fetchCaregiverLogic.pending]: (state) => {
    state.caregiverData = {};
    state.caregiverLoading = true;
    state.caregiverError = false;
  },
  [fetchCaregiverLogic.fulfilled]: (state, { payload }) => {
    state.caregiverData = {};
    state.caregiverData = payload.data;
    state.caregiverLoading = false;
    state.caregiverError = false;
  },
  [fetchCaregiverLogic.rejected]: (state) => {
    state.caregiverData = {};
    state.caregiverError = true;
    state.caregiverLoading = false;
  },

  //* Single Patient Promises
  [singlePatientLogic.pending]: (state) => {
    state.loading = true;
  },
  [singlePatientLogic.fulfilled]: (state, { payload }) => {
    state.singlePatientData = payload.data;
    updatePatientInLists(state, payload.data);
    state.loading = false;
  },
  [singlePatientLogic.rejected]: (state, action) => {
    state.singlePatientData = {};
    state.loading = false;
  },

  [fetchPatientNotesLogic.pending]: (state) => {
    state.patientNotesLoading = true;
  },
  [fetchPatientNotesLogic.fulfilled]: (state, { payload }) => {
    state.patientNotes = payload.data;
    state.patientNotesLoading = false;
  },
  [fetchPatientNotesLogic.rejected]: (state, action) => {
    state.patientNotesLoading = false;
  },

  [changePatientFlagLogic.pending]: (state) => {
    state.flagLoading = true;
  },
  [changePatientFlagLogic.fulfilled]: (state, { payload }) => {
    if (payload?.data) {
      state.singlePatientData = payload?.data;
      updatePatientInLists(state, payload.data);
    }
    notify("Flag changed successfully!");
    state.flagLoading = false;
  },
  [changePatientFlagLogic.rejected]: (state, action) => {
    notify("Something went wrong while chaning patient's flag!", "error");
    state.flagLoading = false;
  },

  //* DIAGNOSIS DATA PROMISES
  [fetchDiagnosisDataLogic.pending]: (state) => {
    state.loading = true;
  },
  [fetchDiagnosisDataLogic.fulfilled]: (state, { payload }) => {
    state.singlePatientData = {
      ...state.singlePatientData,
      flag: flagLookup[payload?.data?.diagnosis] || "RED",
    };
    state.diagnosisData = payload.data;
    state.loading = false;
  },
  [fetchDiagnosisDataLogic.rejected]: (state, action) => {
    state.diagnosisData = null;
    state.loading = false;
  },

  [setDiagnosisDataLogic.pending]: (state) => {
    state.patientNotesLoading = true;
  },
  [setDiagnosisDataLogic.fulfilled]: (state, { payload }) => {
    state.patientNotes = [payload.data, ...state.patientNotes];
    state.diagnosisData = payload.data;
    state.patientNotesLoading = false;
    notify("Notes Added Successfully");
  },
  [setDiagnosisDataLogic.rejected]: (state, action) => {
    state.diagnosisData = null;
    state.patientNotesLoading = false;
    notify("Something went wrong", "info");
  },

  // handle MRN Number
  [handleMrnNumber.pending]: (state) => {
    state.mrnNumberLoading = true;
  },
  [handleMrnNumber.fulfilled]: (state, { payload }) => {
    console.log(payload, 'payload')
    state.singlePatientData = payload || {};
    updatePatientInLists(state, payload);
    state.mrnNumberLoading = false;
  },
  [handleMrnNumber.rejected]: (state) => {
    state.mrnNumberLoading = false;
  },

  // update patient information
  [handleUpdatePatientInfo.pending]: (state) => {
    state.updatePatientInfoLoading = true;
  },
  [handleUpdatePatientInfo.fulfilled]: (state, { payload }) => {
    console.log(payload, 'payload')
    state.singlePatientData = payload || {};
    updatePatientInLists(state, payload);
    state.updatePatientInfoLoading = false;
  },
  [handleUpdatePatientInfo.rejected]: (state) => {
    state.updatePatientInfoLoading = false;
  },

  // Delete Diagnosis Patient Logic
  [deleteDiagnosisDataLogic.pending]: (state) => {
    state.deleteDiagnosisPatientLoading = true;
    state.deleteDiagnosisPatientError = null;
  },
  [deleteDiagnosisDataLogic.fulfilled]: (state, { meta }) => {
    state.deleteDiagnosisPatientLoading = false;
    const deletedId = meta.arg;
    state.patientNotes = state.patientNotes.filter(item => item.id !== deletedId?.id);
    notify("Note Deleted Successfully");
  },
  [deleteDiagnosisDataLogic.rejected]: (state, action) => {
    state.deleteDiagnosisPatientLoading = false;
    state.deleteDiagnosisPatientError = action.error.message || "Delete failed.";
  },

  // Update Diagnosis Patient Logic
  [updateDiagnosisDataLogic.pending]: (state) => {
    state.updateDiagnosisPatientLoading = true;
    state.updateDiagnosisPatientError = null;
  },
  [updateDiagnosisDataLogic.fulfilled]: (state, { payload }) => {
    state.updateDiagnosisPatientLoading = false;
    // update the patientNotes List
    const index = state.patientNotes.findIndex(item => item.id === payload.id);
    if (index !== -1) state.patientNotes[index] = payload;
    notify("Note Updated Successfully");
  },
  [updateDiagnosisDataLogic.rejected]: (state, action) => {
    state.updateDiagnosisPatientLoading = false;
    state.updateDiagnosisPatientError = action.error.message || "Update failed.";
  },

  // Mark Patient Reviewed Logic
  [markPatientReviewed.pending]: (state) => {
    state.loading = false; // We don't want a full page loader here
  },
  [markPatientReviewed.fulfilled]: (state, { payload }) => {
    state.singlePatientData = {
      ...state.singlePatientData,
      ...payload
    };
    // update it to all patient list
    const updatedPatientData = state.patientData.map((patient) => {
      if (patient.id === payload.patientId) {
        return {
          ...patient,
          ...payload,
        };
      }
      return patient;
    });
    state.patientData = updatedPatientData;
    notify("Patient marked as reviewed!");
  },
  [markPatientReviewed.rejected]: (state, action) => {
    notify(action.payload || "Failed to mark patient as reviewed!", "error");
  },

};

const patientSlice = createSlice({
  name: "patient",
  initialState,
  extraReducers: {
    ...patientAPICalls,
  },
  reducers: {
    fetchPatient: patientLogic,
    fetchBHIPatients: bhiPatientLogic,
    fetchInvitedPatient: invitedPatientLogic,
    changePatientFlag: changePatientFlagLogic,
    singlePatient: singlePatientLogic,
    fetchCaregiver: fetchCaregiverLogic,
    alterSinglePatient: alterSinglePatientData,
    fetchDiagnosis: fetchDiagnosisDataLogic,
    setDiagnosis: setDiagnosisDataLogic,
    fetchPatientNotes: fetchPatientNotesLogic,
    alterChatStatus: alterChatStatusLogic,
    alterSSRIStatus: alterSSRIStatusLogic,
    alertFireFlag: alertFireFlagLogic,
    fetchPatientPhysicians: getPatientPhysicians,
    alterMrnNumber: handleMrnNumber,
    deleteDiagnosisDataLogic: deleteDiagnosisDataLogic,
    updateDiagnosisDataLogic: updateDiagnosisDataLogic,
  },
});

export const {
  fetchPatient,
  fetchBHIPatients,
  fetchInvitedPatient,
  changePatientFlag,
  singlePatient,
  fetchCaregiver,
  alterSinglePatient,
  fetchDiagnosis,
  setDiagnosis,
  fetchPatientNotes,
  alterChatStatus,
  alterSSRIStatus,
  alertFireFlag,
  fetchPatientPhysicians,
  alterMrnNumber

} = patientSlice.actions;

export default patientSlice.reducer;